import React, { useState, useEffect } from 'react'
import { Sound, Album, Placeholder } from '@components/app/media'
import { Button } from '@components/common/button'
import Spinner from '@components/common/spinner'
import { FormattedMessage } from 'react-intl'
import { getUserLatest } from '@/data/api/user'
import TrailAnimation from '@/components/common/trail-animation'
import { MediaIndex } from '@/interfaces/algolia'

const PAGE_SIZE = 20

interface Props {
  username: string
  name: string
}

const Latest = ({ username, name }: Props) => {
  const [ nextPage, setNextPage ] = useState(1)
  const [ isLoading, setIsLoading ] = useState(true)
  const [ latest, setLatest ] = useState<MediaIndex[]>([])
  const [ hasMore, setHasMore ] = useState(true)

  const getLatest = async (current) => {
    setIsLoading(true)
    const latest = await getUserLatest(username, nextPage, PAGE_SIZE)
    if (!latest || latest.length === 0) {
      setIsLoading(false)
      setHasMore(false)
      return
    }
    const result = latest.map((item) => item.type === 'sound'
      ? {
        ...item.sound,
        mediaType: 'sound',
      }
      : {
        ...item.album,
        mediaType: 'album',
      })
    setIsLoading(false)
    setNextPage(nextPage + 1)
    setLatest(current.concat(result))
    if (latest.length < PAGE_SIZE) {
      setHasMore(false)
    }
  }

  // load on mount
  useEffect(() => {
    setNextPage(1)
    setIsLoading(true)
    setLatest([])
    setHasMore(true)
    getLatest([])
  }, [ username ])

  if (!isLoading && latest.length === 0) {
    return (
      <p className="tx-center">
        <FormattedMessage
          id="user.latest.missing"
          // eslint-disable-next-line max-len
          defaultMessage="{name} hasn’t been active on SOUND ON LIVE yet.{br}Check back later to see their sounds, claps, and playlist."
          values={{
            name,
            br: <br />,
          }}
        />
      </p>
    )
  }

  return (
    <>
      <h2 className="heading">
        <span className="text tx-oswald">Latest Sounds</span>
      </h2>
      {latest.map((media) => (
        <TrailAnimation key={media.id} >
          {media.mediaType === 'sound' ? (
            <Sound sound={media} />
          ) : (
            <Album album={media} />
          )}
        </TrailAnimation>
      ))}

      {isLoading && latest.length === 0 && (
        <Placeholder />
      )}

      {isLoading && latest.length > 0 && (
        <div className="tx-center mb-5">
          <Spinner size="1.25rem" />
          <span className="tx-oswald tx-w300 ml-2">Loading</span>
        </div>
      )}

      {hasMore && !isLoading && latest.length > 0 && (
        <div className="tx-center mb-5">
          <Button
            className="lg"
            onClick={() => getLatest(latest)}
          >
            Load more
          </Button>
        </div>
      )}
    </>
  )
}

export default Latest
